/**
 * Typed event taxonomy — `analytics.<event>(props)` helpers layered over the
 * raw `track()` call from `analytics.ts`.
 *
 * Source of truth: `plans/03-architecture/observability.md` §"Product analytics"
 * + `plans/04-build-plan/web-app/agent-prompts/09-observability-prompt.md`
 * §"PostHog".
 *
 * Privacy posture: every event payload carries `userId` only — no email, no
 * IP. The redaction allowlist in `redact.ts` runs again inside `track()` as a
 * belt-and-braces guard against accidental PII bleed.
 *
 * Web Vitals get their own helper because they're not in the agent prompt's
 * 26-event list but the wave spec wires them through PostHog (US-246).
 */
import { track } from './analytics.js';
function emit(event, props) {
    // Translate { userId } → distinct_id for PostHog stitching while keeping the
    // typed surface ergonomic at call sites.
    const generic = props;
    const { userId, ...rest } = generic;
    const out = { ...rest };
    if (typeof userId === 'string' && userId.length > 0) {
        out['distinct_id'] = userId;
        out['userId'] = userId;
    }
    track(event, out);
}
// ─── Auth + onboarding ─────────────────────────────────────────────────────
function signup(props) {
    emit('signup', props);
}
function login(props) {
    emit('login', props);
}
function verifyEmail(props) {
    emit('verify_email', props);
}
function passwordResetCompleted(props) {
    emit('password_reset_completed', props);
}
function onboardingStepCompleted(props) {
    emit('onboarding.step_completed', props);
}
function onboardingCompleted(props) {
    emit('onboarding.completed', props);
}
function onboardingAbandoned(props) {
    emit('onboarding.abandoned', props);
}
// ─── Account ────────────────────────────────────────────────────────────────
function accountConnected(props) {
    emit('account.connected', props);
}
function accountDisconnected(props) {
    emit('account.disconnected', props);
}
// ─── Voice ──────────────────────────────────────────────────────────────────
function voiceAnalysed(props) {
    emit('voice.analysed', props);
}
function voiceSuggestionApplied(props) {
    emit('voice.suggestion_applied', props);
}
// ─── Business + campaign ───────────────────────────────────────────────────
function businessScanCompleted(props) {
    emit('business.scan_completed', props);
}
function campaignCreated(props) {
    emit('campaign.created', props);
}
function campaignLaunched(props) {
    emit('campaign.launched', props);
}
function campaignCompleted(props) {
    emit('campaign.completed', props);
}
// ─── Inbox ──────────────────────────────────────────────────────────────────
function inboxDraftApproved(props) {
    emit('inbox.draft_approved', props);
}
function inboxDraftRegenerated(props) {
    emit('inbox.draft_regenerated', props);
}
function inboxOutcomeReported(props) {
    emit('inbox.outcome_reported', props);
}
// ─── Leads ──────────────────────────────────────────────────────────────────
function leadPushed(props) {
    emit('lead.pushed', props);
}
function leadExported(props) {
    // Spec uses `lead.exported_<provider>` so the dashboards can filter by
    // funnel without extra props. Provider is also kept on the payload for
    // grouped queries.
    emit(`lead.exported_${props.provider}`, props);
}
// ─── Subscription ──────────────────────────────────────────────────────────
function subscriptionCreated(props) {
    emit('subscription.created', props);
}
function subscriptionUpgraded(props) {
    emit('subscription.upgraded', props);
}
function subscriptionDowngraded(props) {
    emit('subscription.downgraded', props);
}
function subscriptionCanceled(props) {
    emit('subscription.canceled', props);
}
// ─── Extension ──────────────────────────────────────────────────────────────
function extensionPaired(props) {
    emit('extension.paired', props);
}
function extensionHealthChanged(props) {
    emit('extension.health_changed', props);
}
// ─── Web Vitals (US-246) ───────────────────────────────────────────────────
function webVitals(props) {
    // Web Vitals are not user-scoped at capture time; PostHog will associate
    // them with the current distinct_id from the SDK's persistence layer when
    // identify() has run.
    emit('web_vitals', props);
}
// ─── Public surface ────────────────────────────────────────────────────────
export const analytics = {
    // Auth
    signup,
    login,
    verifyEmail,
    passwordResetCompleted,
    // Onboarding
    onboardingStepCompleted,
    onboardingCompleted,
    onboardingAbandoned,
    // Account
    accountConnected,
    accountDisconnected,
    // Voice
    voiceAnalysed,
    voiceSuggestionApplied,
    // Business
    businessScanCompleted,
    // Campaign
    campaignCreated,
    campaignLaunched,
    campaignCompleted,
    // Inbox
    inboxDraftApproved,
    inboxDraftRegenerated,
    inboxOutcomeReported,
    // Leads
    leadPushed,
    leadExported,
    // Subscription
    subscriptionCreated,
    subscriptionUpgraded,
    subscriptionDowngraded,
    subscriptionCanceled,
    // Extension
    extensionPaired,
    extensionHealthChanged,
    // Web Vitals
    webVitals,
};
/** Names of every typed event helper this module exposes. Test fixture. */
export const ANALYTICS_EVENT_HELPERS = Object.keys(analytics);
//# sourceMappingURL=events.js.map